// https://raw.githubusercontent.com/michaelficarra/bigint-serialiser/refs/heads/master/index.js

// Set constants for VLQ encoding
const SIGNIFICANT_BITS = 7n
const CONTINUE = 1n << SIGNIFICANT_BITS // 128n
const REST_MASK = CONTINUE - 1n // 127n

/**
 * Converts a BigInt to a Uint8Array using variable-length quantity (VLQ) encoding.
 * Uses ZigZag encoding for negative values to efficiently encode signed integers.
 *
 * @param value - The BigInt to convert
 * @returns A Uint8Array containing the VLQ-encoded BigInt
 *
 * @example
 * ```ts
 * import { BigIntToUint8 } from "@pdfking/tfunc"
 *
 * // Positive BigInt
 * const bytes1 = BigIntToUint8(42n);
 *
 * // Negative BigInt
 * const bytes2 = BigIntToUint8(-42n);
 *
 * // Large BigInt
 * const bytes3 = BigIntToUint8(9007199254740991n);
 *
 * // Use for hashing
 * const hash = await UInt8Hash([BigIntToUint8(12345678901234567890n)]);
 * ```
 */
export const BigIntToUint8 = (value: bigint): Uint8Array => {
  // Handle value sign - encode negative values using ZigZag encoding
  if (value < 0n) {
    value = -value
    --value
    value <<= 1n
    value |= 1n
  } else {
    value <<= 1n
  }

  // Calculate required length
  let length = 1
  let temp = value
  while (temp >= CONTINUE) {
    temp >>= SIGNIFICANT_BITS
    --temp
    ++length
  }

  // Create array and fill it
  const bytes = new Uint8Array(length)
  let offset = 0

  while (value >= CONTINUE && offset < bytes.length) {
    bytes[offset] = Number((value & REST_MASK) | CONTINUE)
    value >>= SIGNIFICANT_BITS
    --value
    ++offset
  }

  bytes[offset] = Number(value)

  return bytes
}
